import { httpClient } from '../api/client';

// Different from AddressVerificationStatus, which includes "unverified"
// The address validation endpoint can only return these statuses
export const VALIDATED_ADDRESS_STATUS = {
  ERROR: 'Error',
  WARNING: 'Warning',
  VERIFIED: 'Verified',
} as const;

export type ValidatedAddressStatus = (typeof VALIDATED_ADDRESS_STATUS)[keyof typeof VALIDATED_ADDRESS_STATUS];

export const VERIFICATION_STATUS_CODE = {
  VERIFIED: 'VER',
  UNVERIFIED: 'UNV',
  WARNING: 'WRN',
  ERROR: 'ERR',
} as const;

export type VerificationStatusCode = (typeof VERIFICATION_STATUS_CODE)[keyof typeof VERIFICATION_STATUS_CODE];

/**
 * Numeric enum from the API response (ResidentialIndicator C# enum).
 * 0 = Unknown, 1 = Residential, 2 = Commercial.
 */
export const RESIDENTIAL_INDICATOR_VALUE = {
  UNKNOWN: 0,
  RESIDENTIAL: 1,
  COMMERCIAL: 2,
} as const;

export type ResidentialIndicatorValue = (typeof RESIDENTIAL_INDICATOR_VALUE)[keyof typeof RESIDENTIAL_INDICATOR_VALUE];

export const RESIDENTIAL_INDICATOR = {
  RESIDENTIAL: 'Residential',
  COMMERCIAL: 'Commercial',
  UNKNOWN: 'Unknown',
} as const;

export type ResidentialIndicator = (typeof RESIDENTIAL_INDICATOR)[keyof typeof RESIDENTIAL_INDICATOR];

// Mirrors MatchedAddressResponse, not the standard WebV3Api address contract
export type MatchedAddress = {
  line1: string;
  line2: string;
  line3: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  company: string;
  /** @deprecated Use residentialIndicator instead. */
  isResidential: boolean | null;
  residentialIndicator: ResidentialIndicator;
};

// Mirrors OriginalAddressResponse, not the standard WebV3Api address contract
export type OriginalAddress = {
  line1: string;
  line2: string;
  line3: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  company: string;
};

export type ValidationMessage = {
  type: string;
  message: string;
};

export type AddressValidationResultItem = {
  status: ValidatedAddressStatus;
  matchedAddress: MatchedAddress | null;
  originalAddress: OriginalAddress | null;
  messages: string[];
  validationMessages: ValidationMessage[];
};

// Mirrors ValidateAddressRequest, not the standard WebV3Api address contract
export type ValidateAddressRequest = {
  company: string;
  line1: string;
  line2: string;
  line3: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
};

export const validateAddress = async (request: ValidateAddressRequest): Promise<AddressValidationResultItem | null> => {
  const response = await httpClient.post('/address/validate', [request]);
  return response.data[0] ?? null;
};

export type ParsedAddress = {
  name: string;
  company: string;
  line1: string;
  line2: string;
  line3: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  phone: string;
};

export const parseAddress = async (rawText: string, signal?: AbortSignal): Promise<ParsedAddress> => {
  const response = await httpClient.post('/address/parse', { address: rawText }, { signal });
  return response.data as ParsedAddress;
};
